import { isUndefined } from 'lodash';
import { DateTime } from 'luxon';
import {
  addAccreditationFiles,
  addFieldsToFormData,
} from 'shared/utils/file-handling/file-handling-utils';
import {
  AddAndAcceptAccreditationParams,
  CertificateDataOGP,
  CertificateWithFileUrl,
} from './accreditations-service.types';
import { OgpApiService } from './ogp-api-service';

type BackendEmployeeCertificateItem = {
  accreditationId: string;
  certificateId: string;
  certificateName: string;
  hasFile: boolean;
  startDate: string | null;
  expirationDate: string | null;
  validUntil: string | null;
  type: string;
  state: string;
  isTrusted: boolean;
};

type BackendGetCertificateFileUrlResponse = {
  url: string | null;
};

type BackendGetEmployeeCertificatesResponse = {
  items: BackendEmployeeCertificateItem[];
  totalRows: number;
  nextPage?: number;
  previousPage?: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

class AccreditationsService {
  constructor(private readonly ogpApiService: OgpApiService) {}

  public async getAccreditations(flexWorkerId: string) {
    const response = await this.ogpApiService.get<CertificateDataOGP[]>({
      path: `accreditations/${flexWorkerId}`,
    });
    return response;
  }

  public async getEmployeeCertificates(
    flexWorkerId: string,
    pageParam: number,
    pageSizeParam: number
  ): Promise<BackendGetEmployeeCertificatesResponse> {
    const response = await this.ogpApiService.get<BackendGetEmployeeCertificatesResponse>({
      path: `workforce/${flexWorkerId}/certificates`,
      params: {
        pageParam,
        pageSizeParam,
      },
    });
    return response;
  }

  public async getCertificateFileUrl(
    flexWorkerId: string,
    accreditationId: string
  ): Promise<BackendGetCertificateFileUrlResponse> {
    const response = await this.ogpApiService.get<BackendGetCertificateFileUrlResponse>({
      path: `workforce/${flexWorkerId}/certificates/${accreditationId}/file-url`,
    });
    return response;
  }

  public async trustAccreditation(flexWorkerId: string, accreditationId: string): Promise<void> {
    await this.ogpApiService.create({
      path: `workforce/${flexWorkerId}/certificates/${accreditationId}/trust`,
    });
  }

  public async untrustAccreditation(flexWorkerId: string, accreditationId: string): Promise<void> {
    await this.ogpApiService.delete({
      path: `workforce/${flexWorkerId}/certificates/${accreditationId}/trust`,
    });
  }

  public async updateCertificateValidUntil(
    flexWorkerId: string,
    accreditationId: string,
    validUntil: DateTime | null
  ): Promise<void> {
    await this.ogpApiService.update<void>({
      path: `workforce/${flexWorkerId}/certificates/${accreditationId}/valid-until`,
      body: {
        validUntil: validUntil ? validUntil.toISODate() : null,
      },
    });
  }

  public async getCertificates(): Promise<CertificateWithFileUrl[]> {
    const response = await this.ogpApiService.get<CertificateWithFileUrl[]>({
      path: 'accreditations/certificates',
    });
    return response;
  }

  public async addAndAcceptAccreditation({
    startDate,
    expirationDate,
    accreditations,
    flexWorkerId,
    accreditationType,
  }: AddAndAcceptAccreditationParams): Promise<void> {
    const formData = getWorkerPayloadFormData({
      expirationDate,
      accreditations,
      startDate,
      accreditationType,
    });
    await this.ogpApiService.create<void>({
      body: formData,
      path: `accreditations/${flexWorkerId}/add-and-approve`,
    });
  }
}

function getWorkerPayloadFormData(body: {
  accreditations?: AddAndAcceptAccreditationParams['accreditations'];
  startDate: AddAndAcceptAccreditationParams['startDate'];
  expirationDate: AddAndAcceptAccreditationParams['expirationDate'];
  accreditationType: AddAndAcceptAccreditationParams['accreditationType'];
}): FormData {
  const formData = new FormData();

  let accreditations = '';
  if (!isUndefined(body.accreditations)) {
    addAccreditationFiles(formData, body.accreditations);

    accreditations = JSON.stringify(
      body.accreditations?.map((accreditation) => accreditation.id) ?? []
    );
  }

  const bodyWithoutFiles: { [key: string]: string | undefined } = {
    ...(accreditations.length > 0 && { accreditations }),
    expirationDate: body.expirationDate ? body.expirationDate.toFormat('yyyy-MM-dd') : undefined,
    startDate: body.startDate ? body.startDate.toFormat('yyyy-MM-dd') : undefined,
    accreditationType: body.accreditationType,
  };

  addFieldsToFormData(formData, bodyWithoutFiles);

  return formData;
}

export { AccreditationsService };
